import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Avatar,
  Typography,
  Link,
  Stack,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/X";
import YouTubeIcon from "@mui/icons-material/YouTube";
import FacebookIcon from "@mui/icons-material/Facebook";

import profilepic from "../assets/contact.jpg";

const fontsize = "20px";

interface ContactItemProps {
  icon: React.ReactNode;
  label: string;
  href?: string;
}

function ContactItem({ icon, label, href }: ContactItemProps) {
  const content = (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{
        cursor: href ? "pointer" : "default",
        transition: "all 0.25s ease",
        px: 1,
        py: 1,
        borderRadius: 2,
        "&:hover": {
          transform: "translateY(-2px)",
          backgroundColor: "action.hover",
        },
        "& svg": {
          transition: "color 0.25s ease",
        },
        "&:hover svg": {
          color: "primary.main",
        },
      }}
    >
      {icon}
      <Typography
        variant="body1"
        sx={{ color: "text.primary", fontSize: "1.1rem" }}
      >
        {label}
      </Typography>
    </Stack>
  );

  return href ? (
    <Link
      href={href}
      underline="none"
      color="inherit"
      target={href.startsWith("http") ? "_blank" : undefined}
      rel="noopener noreferrer"
    >
      {content}
    </Link>
  ) : (
    content
  );
}

export default function ContactSection() {
  const { t } = useTranslation();
  return (
    <Box
      sx={(theme) => ({
        width: "100%",
        backgroundRepeat: "no-repeat",

        backgroundImage:
          "radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 90%), transparent)",
        ...theme.applyStyles("dark", {
          backgroundImage:
            "radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 16%), transparent)",
        }),
      })}
    >
      <Box
        id="contact"
        sx={{
          pt: { xs: 14, md: 20 },
          px: { xs: 2, md: 0 },
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Card
          elevation={3}
          sx={{
            width: { xs: "100%", lg: "60%" },
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: { xs: 3 } }}>
            <Grid container spacing={4} alignItems="center">
              {/* Left: Avatar */}
              <Grid
                size={{ xs: 12, md: 3 }}
                display="flex"
                justifyContent="center"
              >
                <Avatar
                  alt="Profile picture"
                  src={profilepic} // replace with your image path
                  sx={{
                    width: { xs: 160, md: 200 },
                    height: { xs: 160, md: 200 },
                  }}
                />
              </Grid>

              {/* Contact Info */}
              <Grid size={{ xs: 12, md: 9 }}>
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  sx={{ color: "text.primary" }}
                >
                  {t("contact.title")}
                </Typography>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: 1 }}
                >
                  {t("contact.intro")}
                </Typography>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <ContactItem
                    icon={<EmailIcon sx={{ fontSize: fontsize }} />}
                    label="sharkshooter2011@gmail.com"
                    href="mailto:sharkshooter2011@gmail.com"
                  />

                  <ContactItem
                    icon={<InstagramIcon sx={{ fontSize: fontsize }} />}
                    label="@sharkshooter2011"
                    href="https://www.instagram.com/sharkshooter2011/"
                  />

                  <ContactItem
                    icon={<TwitterIcon sx={{ fontSize: fontsize }} />}
                    label="@neki2011"
                    href="https://x.com/neki2011"
                  />

                  <ContactItem
                    icon={<YouTubeIcon sx={{ fontSize: fontsize }} />}
                    label="YouTube Channel: @sharkshooter2011"
                    href="https://www.youtube.com/@sharkshooter2011"
                  />

                  <ContactItem
                    icon={<FacebookIcon sx={{ fontSize: fontsize }} />}
                    label="Facebook Page: Muneki Samejima"
                    href="https://www.facebook.com/muneki.samejima"
                  />
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
